import React, { useEffect, useState } from 'react';
import ReactModal, { Props as ReactModalProps } from 'react-modal';
import { ClassNames, SerializedStyles } from '@emotion/react';

import { useCancelScrollPage } from '../../hooks';
import { Button } from '../../common/Button';
import { IconSvg } from '../IconSvg';
import { Typography } from '../Typography';
import { Box } from '../Box';
import { Colors } from '../Colors';
import {
  className,
  Container,
  Preheader,
  Header,
  buttonClose,
  Content,
  Footer,
  Form,
  CentredContent
} from './styles';

export const sizeProp = ['small', 'medium', 'big'] as const;
export type SizePropType = typeof sizeProp[number] | number;

export interface Props {
  size?: SizePropType;
  title?: string;
  isClosable?: boolean;
  isOpen?: boolean;
  isPreloader?: boolean;
  preheader?: string;
  styles?: {
    content?: SerializedStyles;
    plate?: SerializedStyles;
  };
}

export const CLOSE_TIMEOUT_MS = 200;

const Preloader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
    <IconSvg src="preloader" width={44} height={44} />
  </Box>
);

export const Modal: React.FC<Props & ReactModalProps> & ModalExtensions = ({
  size = 'small',
  isClosable = true,
  title,
  isPreloader,
  preheader,
  styles,
  onAfterOpen,
  onAfterClose,
  ...other
}) => {
  const [isOpen, setIsOpen] = useState(other.isOpen);
  const [cancelScrollPage, restoreScrollPage] = useCancelScrollPage();
  let timeoutClose: number | undefined;

  const onRequestCloseCustom = () => {
    setIsOpen(false);
    timeoutClose = other.onRequestClose && setTimeout(other.onRequestClose, CLOSE_TIMEOUT_MS);
  };

  const onAfterOpenCustom = () => {
    cancelScrollPage();
    onAfterOpen && onAfterOpen();
  };

  const onAfterCloseCustom = () => {
    restoreScrollPage();
    onAfterClose && onAfterClose();
  };

  useEffect(() => {
    return () => {
      clearTimeout(timeoutClose);
    };
  }, []);

  useEffect(() => {
    setIsOpen(other.isOpen);
  }, [other.isOpen]);

  return (
    <ClassNames>
      {({ css }) => (
        <ReactModal
          ariaHideApp={false}
          overlayClassName={{
            base: css(className.overlay),
            afterOpen: css(className.overlayAfterOpen),
            beforeClose: css(className.overlayBeforeClose)
          }}
          className={{
            base: css(className.content(size), styles?.content),
            afterOpen: css(className.contentAfterOpen),
            beforeClose: css(className.contentBeforeClose)
          }}
          {...other}
          isOpen={isOpen}
          closeTimeoutMS={CLOSE_TIMEOUT_MS}
          onRequestClose={onRequestCloseCustom}
          onAfterOpen={onAfterOpenCustom}
          onAfterClose={onAfterCloseCustom}
        >
          {preheader && (
            <Preheader>
              <Typography.Text size="extra-small" caps={true} color={Colors.rgbaWhite50}>
                {preheader}
              </Typography.Text>
            </Preheader>
          )}

          <Container isClosable={isClosable} css={styles?.plate}>
            {title && (
              <Header>
                <Typography.Title level={1}>{title}</Typography.Title>
              </Header>
            )}

            {isPreloader ? <Preloader /> : other.children}

            {isClosable && (
              <Button
                view="transparent"
                shape="circle"
                aria-label="Close"
                data-testid="modalCloseBtn"
                onClick={onRequestCloseCustom}
                css={buttonClose}
              >
                <IconSvg src="close" />
              </Button>
            )}
          </Container>
        </ReactModal>
      )}
    </ClassNames>
  );
};

type ModalExtensions = {
  Header: typeof Header;
  Content: typeof Content;
  Footer: typeof Footer;
  Form: typeof Form;
  CentredContent: typeof CentredContent;
  Preloader: typeof Preloader;
};

Modal.Header = Header;
Modal.Content = Content;
Modal.Footer = Footer;
Modal.Form = Form;
Modal.CentredContent = CentredContent;
Modal.Preloader = Preloader;
